import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  Box,
  Input,
  Button,
  Table,
  TableCaption,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from "@chakra-ui/react";

const PurchaseReport = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [startDate, setStartDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const [endDate, setEndDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const [searchTerm, setSearchTerm] = useState("");

  const customerId = localStorage.getItem("customerId");

  const fetchData = async () => {
    if (!startDate || !endDate || !customerId) return;

    try {
      const response = await axios.get(
        `https://api.rasoi.launchlysoftware.in/api/getPurchaseBill/${customerId}?startDate=${startDate}&endDate=${endDate}`,
      );

      const purchases = response.data.purchaseFilter || [];

      const normalized = purchases.map((p) => ({
        ...p,
        item: Array.isArray(p.item) ? p.item : [],
      }));

      setData(normalized);
      setFilteredData(normalized);

      toast.success(response.data.message || "Data fetched");
    } catch (error) {
      toast.error("Failed to fetch data");
      console.error("Fetch error:", error);
    }
  };

  const exportToExcel = () => {
    const excelData = filteredData.map((purchase) => ({
      Name: purchase.name,
      BillNo: purchase.billNo,
      Store: purchase.store,
      Date:
        purchase.date ||
        new Date(purchase.createdAt).toISOString().split("T")[0],
      Address: purchase.address,
      GrossAmount: purchase.grossAmount,
      NetAmount: purchase.netAmount,
      ChallanNo: purchase.challanNo,
      InvoiceNo: purchase.invoiceNo,
      PurchaseItems: purchase.item
        ?.map(
          (itm, idx) =>
            `${idx + 1}: Name-${itm.itemName}, Qty-(${itm.quantity}), Unit-(${
              itm.unit
            }), MRP-(${itm.mrp}), PurchaseRate-(${
              itm.purchaseRate
            }), Discount-(${itm.discount}), SGST-(${itm.sgst}), CGST-(${
              itm.cgst
            }), Amount-(${itm.amount})`,
        )
        .join("; "),
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "PurchaseData");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "PurchaseData.xlsx");
  };

  // 🔍 Search filter function
  const handleSearch = (value) => {
    setSearchTerm(value);

    if (!value.trim()) {
      setFilteredData(data);
      return;
    }

    const lowerValue = value.toLowerCase();

    const filtered = data.filter((purchase) =>
      purchase.item.some((itm) =>
        itm.itemName?.toLowerCase().includes(lowerValue),
      ),
    );

    setFilteredData(filtered);
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, customerId]);

  return (
    <Box>
      <Box mb={4} display="flex" alignItems="center" gap={4}>
        <Input
          w="20%"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <Input
          w="20%"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <Input
          w="30%"
          type="text"
          placeholder="Search by Item Name"
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <Button onClick={exportToExcel} colorScheme="blue">
          Export to Excel
        </Button>
      </Box>

      <TableContainer maxW="100%">
        <Table size="sm" variant="striped" colorScheme="green">
          <TableCaption>Purchase Data</TableCaption>
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Date</Th>
              <Th>Address</Th>
              <Th>Balance</Th>
              <Th>Bill No</Th>
              <Th>Challan No</Th>
              <Th>Invoice No</Th>
              <Th>Store</Th>
              <Th>CGST</Th>
              <Th>SGST</Th>
              <Th>Discount</Th>
              <Th>Discount(%)</Th>
              <Th>Total Tax</Th>
              <Th>Gross Amount</Th>
              <Th>Net Amount</Th>
              <Th>Items</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredData.map((item, idx) => (
              <Tr key={idx}>
                <Td>{item.name}</Td>
                <Td>
                  {item.date ||
                    new Date(item.createdAt).toISOString().split("T")[0]}
                </Td>
                <Td>{item.address}</Td>
                <Td>{item.balance}</Td>
                <Td>{item.billNo}</Td>
                <Td>{item.challanNo}</Td>
                <Td>{item.invoiceNo}</Td>
                <Td>{item.store}</Td>
                <Td>{item.cgstTotal}</Td>
                <Td>{item.sgstTotal}</Td>
                <Td>{item.cashDiscountAmount}</Td>
                <Td>{item.cashDiscountPercentage}</Td>
                <Td>{item.totalTax}</Td>
                <Td>{item.grossAmount}</Td>
                <Td>{item.netAmount}</Td>
                <Td>
                  <Table size="xs" variant="striped" colorScheme="blue">
                    <Thead>
                      <Tr>
                        <Th>Name</Th>
                        <Th>Qty</Th>
                        <Th>Unit</Th>
                        <Th>MRP</Th>
                        <Th>Rate</Th>
                        <Th>CGST</Th>
                        <Th>SGST</Th>
                        <Th>Amount</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {item.item.map((itm, i) => (
                        <Tr key={i}>
                          <Td>{itm.itemName}</Td>
                          <Td>{itm.quantity}</Td>
                          <Td>{itm.unit}</Td>
                          <Td>{itm.mrp}</Td>
                          <Td>{itm.purchaseRate}</Td>
                          <Td>{itm.cgst}</Td>
                          <Td>{itm.sgst}</Td>
                          <Td>{itm.amount}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PurchaseReport;
